import { useMemo, useRef, useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { toast } from "@components/toast/Toast";
import Tooltip from "@components/Tooltip";
import { useMerchant } from "@auth";
import { cn } from "@utils";
import SendEmailPreviewModal from "../SmartNoteModal/SendEmailPreviewModal";
import {
  prepareDocumentsForSend,
  sendDocumentEmail,
  downloadDocumentsSet,
} from "@api/documentsController";

// A plain ring spinner (the project's Tailwind overrides @keyframes spin, so `animate-spin` doesn't
// rotate; doc-spin in index.css is a real rotation).
export function Spinner({ className }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        "doc-spin inline-block rounded-full border-2 border-current border-r-transparent",
        className,
      )}
    />
  );
}

export const SINGLE_LINE_TOOLTIP =
  "pointer-events-none block whitespace-nowrap rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-medium leading-snug text-white shadow-lg";

// Minimal, borderless icon action (download / send) — matches the mock's light row actions.
export function GhostIconButton({ icon: Icon, busy, label, tooltip, onClick }) {
  return (
    <Tooltip
      content={tooltip}
      wrapperClassName="flex"
      className={SINGLE_LINE_TOOLTIP}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        aria-label={label}
        className="flex size-8 shrink-0 items-center justify-center rounded-lg text-zinc-600 transition-colors hover:bg-zinc-200/70 hover:text-zinc-800 disabled:opacity-60"
      >
        {busy ? <Spinner className="size-4" /> : <Icon aria-hidden="true" className="size-4" />}
      </button>
    </Tooltip>
  );
}

// Singular / plural wording per document type, and the order they read in a sentence.
const DOC_TYPE_WORDS = {
  Invoice: ["invoice", "invoices"],
  ProofOfDelivery: ["POD", "PODs"],
  CreditNote: ["credit note", "credit notes"],
  Statement: ["statement", "statements"],
};
const DOC_TYPE_ORDER = ["Invoice", "ProofOfDelivery", "CreditNote", "Statement"];

// A natural description of what's attached, by type and count: "your invoices and PODs", "your invoice",
// "your statement and credit notes". Falls back to a generic phrase for anything unrecognised.
function attachedPhrase(docs) {
  const counts = {};
  for (const d of docs) counts[d.documentType] = (counts[d.documentType] ?? 0) + 1;
  const parts = DOC_TYPE_ORDER.filter((type) => counts[type]).map((type) => {
    const [one, many] = DOC_TYPE_WORDS[type];
    return counts[type] === 1 ? one : many;
  });
  if (parts.length === 0) return docs.length === 1 ? "your document" : "your documents";
  const list =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `your ${list}`;
}

// The toast for an action where nothing could be generated — generic, so it reads the same for any count.
function nothingGeneratedMessage(rows) {
  if (rows.length === 1) return `${rows[0].title} couldn't be generated — please try again.`;
  return `None of the ${rows.length} documents could be generated — please try again.`;
}

// Build the suggestion shape SendEmailPreviewModal consumes from a set of materialized documents.
function buildEmailSuggestion(docs, email, customerName, merchantName) {
  const subject = docs.length === 1 ? (docs[0].reference ?? docs[0].fileName ?? "Your document") : "Your documents";
  const greeting = customerName?.trim() ? `Hi ${customerName.trim()},` : "Hi,";
  const signOff = merchantName?.trim() ? `Kind regards,<br>${merchantName.trim()}` : "Kind regards";
  const body = `${greeting}<br><br>Please find ${attachedPhrase(docs)} attached.<br><br>${signOff}`;
  return {
    params: {
      steps: [
        {
          stepType: "email.send",
          stepId: "send-email",
          payload: {
            recipient_email: email ?? "",
            subject,
            title: subject,
            body,
            attachment_locations: docs.map((d) => d.s3Key),
            cc_emails: [],
            bcc_emails: [],
            additional_recipient_emails: [],
          },
        },
      ],
      requestConfig: {
        documents: docs.map((d) => ({
          reference: d.reference,
          documentType: d.documentType,
          previewUrl: d.previewUrl,
          fileName: d.fileName,
        })),
        email,
      },
    },
  };
}

// Rows, not generated files: one POD row can materialize several files, so 15 rows leaves headroom
// under the 20-attachment send limit without needing to know the real file count up front.
export const MAX_SELECTED_DOCUMENTS = 15;

// Selection, download and preview-&-send for document rows ({ id, title, spec, download }), shared by the
// Documents and Balance tabs. Render `sendModal` once in the tab. `onSent` fires after an email goes;
// `onClose` whenever the send modal closes or a send couldn't open (nothing could be generated).
export function useDocumentActions({ customer, detail, onSent, onClose }) {
  const accountCode = detail?.accountCode ?? customer?.accountCode;
  const recipientCustomer = detail ?? customer;
  const { merchant } = useMerchant();
  // Selection is a Map (id → row) rather than a Set of ids, so a selected document survives paging and
  // filter switches — the bulk actions need the row objects, which may no longer be in the loaded page.
  const [selected, setSelected] = useState(() => new Map());
  const [busy, setBusy] = useState(() => new Set());
  // The open send: the documents that were generated, plus the rows that couldn't be (offered for retry).
  const [sendState, setSendState] = useState(null);
  const [sending, setSending] = useState(false);
  // Rows whose last generate attempt failed, flagged in the list until an attempt succeeds.
  const [failedIds, setFailedIds] = useState(() => new Set());

  const withBusy = async (id, fn) => {
    setBusy((prev) => new Set(prev).add(id));
    try {
      await fn();
    } catch (err) {
      toast({ description: err?.message ?? "Something went wrong — please try again." });
    } finally {
      setBusy((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const recordAttempt = (attemptedRows, failedRows) =>
    setFailedIds((prev) => {
      const next = new Set(prev);
      attemptedRows.forEach((r) => next.delete(r.id));
      failedRows.forEach((r) => next.add(r.id));
      return next;
    });

  const prepareRows = async (specRows) => {
    const { docs, failed } = await prepareDocumentsForSend(accountCode, specRows.map((r) => r.spec));
    const failedRows = failed.map((i) => specRows[i]);
    recordAttempt(specRows, failedRows);
    return { docs, failedRows };
  };

  const openSendFor = (specRows, busyKey) =>
    withBusy(busyKey, async () => {
      let opened = false;
      try {
        const { docs, failedRows } = await prepareRows(specRows);
        if (docs.length === 0) {
          toast({ description: nothingGeneratedMessage(failedRows) });
          return;
        }
        setSendState({ docs, failedRows });
        opened = true;
      } finally {
        if (!opened) onClose?.();
      }
    });

  const retryUnavailable = async () => {
    const { docs, failedRows } = await prepareRows(sendState.failedRows);
    if (docs.length === 0) {
      toast({ description: "Those documents still couldn't be generated — please try again later." });
    }
    setSendState((prev) => (prev ? { docs: [...prev.docs, ...docs], failedRows } : prev));
  };

  const downloadRow = (row) =>
    withBusy(row.id, async () => {
      try {
        await row.download();
        recordAttempt([row], []);
      } catch (err) {
        recordAttempt([row], [row]);
        throw err;
      }
    });

  const selectedRows = useMemo(() => [...selected.values()], [selected]);

  const downloadSelected = () =>
    withBusy("download-bulk", async () => {
      const { count, failed } = await downloadDocumentsSet(accountCode, selectedRows.map((r) => r.spec));
      const failedRows = failed.map((i) => selectedRows[i]);
      recordAttempt(selectedRows, failedRows);
      if (count === 0) {
        toast({ description: nothingGeneratedMessage(failedRows) });
      } else if (failedRows.length > 0) {
        toast({
          description: `Downloaded ${count} of ${count + failedRows.length} documents — ${
            failedRows.length === 1 ? "1 couldn't" : `${failedRows.length} couldn't`
          } be generated.`,
        });
      } else if (count > 1) {
        toast({ description: `Downloaded ${count} documents (zipped).` });
      }
    });

  const toggleSelect = (row) => {
    if (!selected.has(row.id) && selected.size >= MAX_SELECTED_DOCUMENTS) {
      toast({ description: `You can select up to ${MAX_SELECTED_DOCUMENTS} documents at a time.` });
      return;
    }
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  };

  // Memoized on the generated docs: the modal re-seeds whenever the suggestion's identity changes, so a
  // fresh object every render would wipe the user's edits on any unrelated re-render.
  const emailSuggestion = useMemo(
    () =>
      sendState
        ? buildEmailSuggestion(
            sendState.docs,
            recipientCustomer?.email,
            recipientCustomer?.name,
            merchant?.name,
          )
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendState?.docs],
  );

  const handleSend = async ({ subject, title, body, attachmentLocations, recipients, cc, bcc }) => {
    setSending(true);
    try {
      const keptDocs = sendState.docs.filter((d) => attachmentLocations.includes(d.s3Key));
      await sendDocumentEmail(customer.id, {
        recipientEmail: recipients[0],
        cc: [...(cc ?? []), ...recipients.slice(1)],
        bcc: bcc ?? [],
        subject,
        title,
        body,
        attachmentKeys: attachmentLocations,
        documents: keptDocs.map((d) => ({
          documentType: d.documentType,
          invoiceNumbers: d.invoiceNumbers ?? [],
          salesOrderNumbers: d.salesOrderNumbers ?? [],
        })),
      });
      toast({ description: "Email sent." });
      onSent?.();
      // Rows that couldn't be generated stay selected, so they're easy to retry or chase separately.
      const unsent = new Set(sendState.failedRows.map((r) => r.id));
      setSelected((prev) => new Map([...prev].filter(([id]) => unsent.has(id))));
      setSendState(null);
    } catch (err) {
      toast({ description: err?.message ?? "The email couldn't be sent — please try again." });
    } finally {
      setSending(false);
    }
  };

  const sendModal = (
    <SendEmailPreviewModal
      isOpen={!!sendState}
      suggestion={emailSuggestion}
      customer={recipientCustomer}
      sending={sending}
      unavailable={sendState?.failedRows.map((r) => r.title) ?? []}
      onRetryUnavailable={retryUnavailable}
      onClose={() => {
        setSendState(null);
        onClose?.();
      }}
      onSend={handleSend}
    />
  );

  return {
    accountCode,
    selected,
    selectedRows,
    clearSelection: () => setSelected(new Map()),
    toggleSelect,
    busy,
    failedIds,
    downloadRow,
    downloadSelected,
    openSendFor,
    sendModal,
  };
}

// Floats at the foot of the tab's scroll container (sticky, not fixed: the tab panels sit in a transformed
// track, which would re-anchor a fixed element). It stays mounted and slides in/out so the list never
// shifts; the last count is held while it slides away.
export function SelectionBar({ actions, noun = "document" }) {
  const { selected, selectedRows, clearSelection, busy, downloadSelected, openSendFor } = actions;
  const visible = selected.size > 0;
  const lastCount = useRef(0);
  if (visible) lastCount.current = selected.size;
  const count = lastCount.current;
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        "sticky bottom-4 z-10 mt-auto transition-[transform,opacity,visibility] duration-300 ease-out",
        visible ? "visible translate-y-0 opacity-100" : "invisible translate-y-[calc(100%+1rem)] opacity-0",
      )}
    >
      <div className="flex items-center justify-between rounded-xl border border-zinc-300 bg-white px-4 py-3 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold text-zinc-900">
            {count} {noun}{count === 1 ? "" : "s"} selected
          </span>
          {count >= MAX_SELECTED_DOCUMENTS && (
            <span className="text-zinc-400">· max {MAX_SELECTED_DOCUMENTS}</span>
          )}
          <button
            type="button"
            onClick={clearSelection}
            className="font-medium text-zinc-400 hover:text-zinc-700"
          >
            Deselect
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={downloadSelected}
            disabled={busy.has("download-bulk")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:text-zinc-900 disabled:opacity-60"
          >
            {busy.has("download-bulk") ? (
              <Spinner className="size-4 text-zinc-400" />
            ) : (
              <ArrowDownTrayIcon aria-hidden="true" className="size-4 text-zinc-400" />
            )}
            Download
          </button>
          <button
            type="button"
            onClick={() => openSendFor(selectedRows, "send-bulk")}
            disabled={busy.has("send-bulk")}
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 disabled:opacity-60"
          >
            {busy.has("send-bulk") ? (
              <Spinner className="size-4 text-white" />
            ) : (
              <PaperAirplaneIcon aria-hidden="true" className="size-4" />
            )}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
